import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
	siteGetSite,
	experimentalLocationGetExperimentalLocations,
	experimentalLocationCreateExperimentalLocation,
	experimentalLocationUpdateExperimentalLocation,
	experimentalLocationDeleteExperimentalLocation,
	sensorGetSensors,
	sensorPostPlacement,
	sensorPatchProperties,
	sensorBulkApplyProperties,
	chamberMappingGetChamberMappings,
	towerMappingGetTowerMappings,
	treatmentsGetSiteTreatments,
} from '../../api';
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	SmartField_Api_Contracts_CoordinateDto,
} from '../../api';
import { useIsSiteManagerForSite } from '../../lib/auth';
import { GeometryDevicesCanvas, type FocusTarget, type FocusChannelTarget } from './GeometryDevicesCanvas';
import { LayerSidebar } from './LayerSidebar';
import { ExperimentalLocationFormPanel } from './ExperimentalLocationFormPanel';
import { SensorPlacementPanel } from './SensorPlacementPanel';
import { Modal } from '../../components/ui/Modal';
import { MappingPanel } from './MappingPanel';

export type PageMode =
	| { kind: 'idle' }
	| { kind: 'editing-el'; el: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto }
	| { kind: 'placing-sensor'; sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[]; coords?: SmartField_Api_Contracts_CoordinateDto };

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

export function SiteGeometryDevicesPage() {
	const { siteId } = useParams<{ siteId: string }>();
	const id = Number(siteId);
	const queryClient = useQueryClient();
	const canEdit = useIsSiteManagerForSite(id);

	const [mode, setMode] = useState<PageMode>({ kind: 'idle' });
	const [visibleLayers, setVisibleLayers] = useState<Set<string>>(new Set(['boundary', 'plots', 'fields', 'blocks', 'miniblocks', 'sensors']));
	const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
	const [focusChannelTarget, setFocusChannelTarget] = useState<FocusChannelTarget | null>(null);
	const [asOf, setAsOf] = useState<string>(todayIso());
	const [highlightedLocationId, setHighlightedLocationId] = useState<number | null>(null);
	const [highlightedChannelKey, setHighlightedChannelKey] = useState<string | null>(null);
const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const channelHighlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const isViewingPast = asOf !== todayIso();

	const { data: site, isLoading: siteLoading } = useQuery({
		queryKey: ['sites', id],
		queryFn: () => siteGetSite({ id }),
		enabled: !isNaN(id),
	});

	const { data: els = [], isLoading: elsLoading } = useQuery({
		queryKey: ['experimental-locations', id, asOf],
		queryFn: () => experimentalLocationGetExperimentalLocations({ siteId: id, asOf }),
		enabled: !isNaN(id),
	});

	const { data: allTimeEls = [] } = useQuery({
		queryKey: ['experimental-locations', id, 'all-time'],
		queryFn: () => experimentalLocationGetExperimentalLocations({ siteId: id, includeAll: true }),
		enabled: !isNaN(id),
	});

	const plots = els.filter((el) => el.state === 'Plot');
	const fields = els.filter((el) => el.state === 'Field');
	const blocks = els.filter((el) => el.state === 'Block');
	const miniBlocks = els.filter((el) => el.state === 'MiniBlock');

	const { data: sensors = [], isLoading: sensorsLoading } = useQuery({
		queryKey: ['sensors', id, asOf],
		queryFn: () => sensorGetSensors({ siteId: id, asOf }),
		enabled: !isNaN(id),
	});

	const asOfYear = new Date(asOf).getUTCFullYear();

	const { data: chambers = [] } = useQuery({
		queryKey: ['chamber-mappings', id, asOfYear],
		queryFn: () => chamberMappingGetChamberMappings({ siteId: id, year: asOfYear }),
		enabled: !isNaN(id),
	});

	const { data: towers = [] } = useQuery({
		queryKey: ['tower-mappings', id, asOfYear],
		queryFn: () => towerMappingGetTowerMappings({ siteId: id, year: asOfYear }),
		enabled: !isNaN(id),
	});

	const { data: siteTreatments = [] } = useQuery({
		queryKey: ['site-treatments', id],
		queryFn: () => treatmentsGetSiteTreatments({ siteId: id }),
		enabled: !isNaN(id),
	});

const createElMutation = useMutation({
		mutationFn: (data: Parameters<typeof experimentalLocationCreateExperimentalLocation>[0]['requestBody']) =>
			experimentalLocationCreateExperimentalLocation({ siteId: id, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['experimental-locations', id] });
			setMode({ kind: 'idle' });
			toast.success('Saved');
		},
		onError: () => toast.error('Failed to save'),
	});

	const updateElMutation = useMutation({
		mutationFn: ({ elId, data }: { elId: number; data: Parameters<typeof experimentalLocationUpdateExperimentalLocation>[0]['requestBody'] }) =>
			experimentalLocationUpdateExperimentalLocation({ siteId: id, id: elId, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['experimental-locations', id] });
			setMode({ kind: 'idle' });
			toast.success('Updated');
		},
		onError: () => toast.error('Failed to update'),
	});

	const deleteElMutation = useMutation({
		mutationFn: (elId: number) => experimentalLocationDeleteExperimentalLocation({ siteId: id, id: elId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['experimental-locations', id] });
			setMode({ kind: 'idle' });
			toast.success('Deleted');
		},
		onError: () => toast.error('Failed to delete'),
	});

	const [lastEffectiveDates, setLastEffectiveDates] = useState<{ from: string; to: string } | null>(null);

	const postPlacementMutation = useMutation({
		mutationFn: async ({ sensorIds, data }: { sensorIds: number[]; data: Parameters<typeof sensorPostPlacement>[0]['requestBody'] }) => {
			const results = await Promise.allSettled(sensorIds.map((sensorId) => sensorPostPlacement({ id: sensorId, siteId: id, requestBody: data })));
			const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
			if (rejected.length > 0) {
				const body = (rejected[0]?.reason as { body?: unknown })?.body;
				const detail = typeof body === 'string' ? `: ${body}` : '';
				throw new Error(`${rejected.length} of ${sensorIds.length} placement(s) failed${detail}`);
			}
		},
		onSuccess: (_data, { sensorIds, data }) => {
			queryClient.invalidateQueries({ queryKey: ['sensors', id] });
			sensorIds.forEach((sensorId) =>
				queryClient.invalidateQueries({ queryKey: ['sensor-placement-history', sensorId] }),
			);
			if (data?.effectiveFromUtc) {
				setLastEffectiveDates({
					from: data.effectiveFromUtc.slice(0, 10),
					to: data.effectiveToUtc ? data.effectiveToUtc.slice(0, 10) : '',
				});
			}
			setMode({ kind: 'idle' });
			toast.success('Placement saved');
		},
		onError: (err: Error) => toast.error(err.message ?? 'Failed to save placement'),
	});

	const patchPropertiesMutation = useMutation({
		mutationFn: ({ sensorId, data }: { sensorId: number; data: Parameters<typeof sensorPatchProperties>[0]['requestBody'] }) =>
			sensorPatchProperties({ id: sensorId, siteId: id, requestBody: data }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['sensors', id] });
			toast.success('Heights saved');
		},
		onError: () => toast.error('Failed to save heights'),
	});

	const bulkApplyPropertiesMutation = useMutation({
		mutationFn: (params: {
			experimentalLocationId: number;
			deviceFilter?: 'ChamberDevices' | 'TowerDevices';
			sensorIds?: number[];
			effectiveFromUtc: string;
			effectiveToUtc: string | null;
			properties: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto;
		}) =>
			sensorBulkApplyProperties({
				siteId: id,
				requestBody: {
					experimentalLocationId: params.experimentalLocationId,
					deviceFilter: params.deviceFilter ?? 'ChamberDevices',
					sensorIds: params.sensorIds,
					effectiveFromUtc: params.effectiveFromUtc,
					effectiveToUtc: params.effectiveToUtc,
					properties: params.properties,
					asOf,
				},
			}),
		onSuccess: (_data, params) => {
			queryClient.invalidateQueries({ queryKey: ['sensors', id] });
			if (params.sensorIds) {
				params.sensorIds.forEach((sensorId) =>
					queryClient.invalidateQueries({ queryKey: ['sensor-placement-history', sensorId] }),
				);
			} else {
				queryClient.invalidateQueries({ queryKey: ['sensor-placement-history'] });
			}
			toast.success('Heights saved');
		},
		onError: () => toast.error('Failed to save heights'),
	});

	const handleBulkApplyChamberHeights = async (
		elId: number,
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => {
		await bulkApplyPropertiesMutation.mutateAsync({
			experimentalLocationId: elId,
			deviceFilter: 'ChamberDevices',
			effectiveFromUtc: effectiveFrom,
			effectiveToUtc: effectiveTo,
			properties: data,
		});
	};

	const handleBulkApplyTowerHeights = async (
		sensorIds: number[],
		effectiveFrom: string,
		effectiveTo: string | null,
		data: SmartField_Api_Contracts_Sensor_DimSensorPropertiesUpdateDto,
	) => {
		const primaryEl = sensors.find((s) => s.id === sensorIds[0])?.currentExperimentalLocationId;
		if (primaryEl == null) {
			throw new Error('Could not resolve experimental location for tower sensors');
		}
		await bulkApplyPropertiesMutation.mutateAsync({
			experimentalLocationId: primaryEl,
			sensorIds,
			effectiveFromUtc: effectiveFrom,
			effectiveToUtc: effectiveTo,
			properties: data,
		});
	};

	const toggleLayer = (layer: string) => {
		setVisibleLayers((prev) => {
			const next = new Set(prev);
			if (next.has(layer)) {
				next.delete(layer);
			} else {
				next.add(layer);
			}
			return next;
		});
	};

	const handleFocusEl = (elId: number) => {
		setFocusTarget({ elId, ts: Date.now() });
		if (highlightTimeoutRef.current) {
			clearTimeout(highlightTimeoutRef.current);
		}
		setHighlightedLocationId(elId);
		highlightTimeoutRef.current = setTimeout(() => setHighlightedLocationId(null), 2000);
	};

	const handleFocusChannel = (channelKey: string) => {
		if (channelHighlightTimeoutRef.current) {
			clearTimeout(channelHighlightTimeoutRef.current);
		}
		setHighlightedChannelKey(channelKey);
		setFocusChannelTarget({ channelKey, ts: Date.now() });
		channelHighlightTimeoutRef.current = setTimeout(() => setHighlightedChannelKey(null), 2000);
	};

const elFormMode = mode.kind === 'editing-el';
	const elFormState = mode.kind === 'editing-el' ? (mode.el.state ?? 'Plot') : 'Plot' as const;
	const elFormEl = mode.kind === 'editing-el' ? mode.el : undefined;

	const isSavingEl = createElMutation.isPending || updateElMutation.isPending;

	if (siteLoading) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
				<div className="flex flex-col items-center gap-3 rounded-xl bg-white px-10 py-8 shadow-xl">
					<svg className="h-8 w-8 animate-spin text-smartfield" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
					</svg>
					<span className="text-sm font-medium text-gray-600">Loading site…</span>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Link to="/sites" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
					← Sites
				</Link>
				<span className="text-sm font-semibold text-gray-700">{site?.name ?? ''}</span>
				<Link to="/data/export" className="inline-flex items-center gap-1.5 text-sm text-smartfield hover:text-smartfield/80">
					Data Export →
				</Link>
			</div>

			<details className="bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
				<summary className="px-4 py-3 cursor-pointer font-semibold select-none">How to use this page</summary>
				<div className="px-4 pb-4 pt-2 space-y-2">
					<p><span className="font-semibold">Adding fields and plots</span> — Expand the Fields or Plots section and use the edit button to update details. Geometry is managed in QGIS.</p>
					<p><span className="font-semibold">Placing a sensor</span> — Find your sensor in the "Unplaced" list on the left. Drag it onto the map to where the sensor is physically installed, or click "Place" to enter coordinates. Link it to the correct plot or field.</p>
					<p><span className="font-semibold">Moving a sensor after relocation</span> — Drag the sensor dot on the map to its new position. A form will appear to confirm the date of the move.</p>
<p><span className="font-semibold">Viewing a past layout</span> — Use the "As of" date picker to see how the site was configured on any past date.</p>
				</div>
			</details>

			{isViewingPast && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
					Viewing as of <span className="font-semibold">{asOf}</span>
				</div>
			)}

			<div className="flex items-center gap-3">
				<label className="text-sm text-gray-600 font-medium">As of</label>
				<input
					type="date"
					value={asOf}
					onChange={(e) => setAsOf(e.target.value)}
					className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
				/>
				{isViewingPast && (
					<button
						type="button"
						onClick={() => setAsOf(todayIso())}
						className="text-xs text-amber-700 underline"
					>
						Back to today
					</button>
				)}
			</div>

			<div className="flex gap-4 w-full h-[calc(100vh-180px)] min-h-[500px]">
				<div className="w-72 flex-shrink-0 flex flex-col gap-4 overflow-y-auto">
					<MappingPanel
						siteId={id}
						year={asOfYear}
						canEdit={canEdit}
						els={els}
						sensors={sensors}
						onBulkApplyChamberHeights={handleBulkApplyChamberHeights}
						onBulkApplyTowerHeights={handleBulkApplyTowerHeights}
						isBulkApplying={bulkApplyPropertiesMutation.isPending}
					/>
					<LayerSidebar
						siteId={id}
						asOf={asOf}
						site={site ?? null}
						plots={plots}
						fields={fields}
						blocks={blocks}
						miniBlocks={miniBlocks}
						sensors={sensors}
						siteTreatments={siteTreatments}
						elsLoading={elsLoading}
						sensorsLoading={sensorsLoading}
						visibleLayers={visibleLayers}
						onToggleLayer={toggleLayer}
						canEdit={canEdit}
						onEditEl={(el) => setMode({ kind: 'editing-el', el })}
						onDeleteEl={(elId) => deleteElMutation.mutate(elId)}
						onPlaceChannel={(channelSensors) => setMode({ kind: 'placing-sensor', sensors: channelSensors })}
						onFocusEl={handleFocusEl}
						onFocusChannel={handleFocusChannel}
					/>
				</div>

				<div className="flex-1 rounded-lg overflow-hidden border border-gray-200">
					<GeometryDevicesCanvas
						site={site ?? null}
						asOf={asOf}
						isViewingPast={isViewingPast}
						plots={plots}
						fields={fields}
						blocks={blocks}
						miniBlocks={miniBlocks}
						sensors={sensors}
						chambers={chambers}
						towers={towers}
						siteTreatments={siteTreatments}
						visibleLayers={visibleLayers}
						mode={mode}
						canEdit={canEdit}
						focusTarget={focusTarget}
						focusChannelTarget={focusChannelTarget}
						highlightedLocationId={highlightedLocationId}
						highlightedChannelKey={highlightedChannelKey}
						placingCoords={mode.kind === 'placing-sensor' ? mode.coords ?? null : null}
						placingSensors={mode.kind === 'placing-sensor' ? mode.sensors : []}
						onSensorDrop={(channelSensors, coords) => setMode({ kind: 'placing-sensor', sensors: channelSensors, coords })}
						onSensorDragEnd={(channelSensors, coords) => setMode({ kind: 'placing-sensor', sensors: channelSensors, coords })}
						onSensorClick={(channelSensors) => setMode({ kind: 'placing-sensor', sensors: channelSensors, coords: channelSensors[0]?.currentGeolocation ?? undefined })}
						onElClick={(el) => setMode({ kind: 'editing-el', el })}
					/>
				</div>
			</div>

			{/* EL create/edit modal */}
			<Modal open={elFormMode} onClose={() => setMode({ kind: 'idle' })} size="xl">
				<ExperimentalLocationFormPanel
					siteId={id}
					el={elFormEl}
					state={elFormState}
					drawnCoords={undefined}
					onCreate={(data) => createElMutation.mutate(data)}
					onUpdate={(data) => {
						if (mode.kind === 'editing-el' && mode.el.id != null) {
							updateElMutation.mutate({ elId: mode.el.id, data });
						}
					}}
					onRedraw={undefined}
					onCancel={() => setMode({ kind: 'idle' })}
					isSaving={isSavingEl}
				/>
			</Modal>


{/* Sensor placement modal */}
			<Modal open={mode.kind === 'placing-sensor'} onClose={() => setMode({ kind: 'idle' })} size="xl">
				{mode.kind === 'placing-sensor' && (
					<SensorPlacementPanel
						sensors={mode.sensors}
						coords={mode.coords}
						plots={plots}
						fields={fields}
						blocks={blocks}
						miniBlocks={miniBlocks}
						allTimeEls={allTimeEls}
						chambers={chambers}
						towers={towers}
						site={site ?? null}
						lastEffectiveDates={lastEffectiveDates}
						onCoordsChange={(coords) => setMode({ kind: 'placing-sensor', sensors: mode.sensors, coords })}
						onSavePlacement={(data) => postPlacementMutation.mutate({ sensorIds: mode.sensors.map((s) => s.id!), data })}
						onSaveHeights={(data) => {
							const primarySensor = mode.sensors[0];
							if (primarySensor?.id != null) {
								patchPropertiesMutation.mutate({ sensorId: primarySensor.id, data });
							}
						}}
						onCancel={() => setMode({ kind: 'idle' })}
						isSavingPlacement={postPlacementMutation.isPending}
						isSavingHeights={patchPropertiesMutation.isPending}
						canEdit={canEdit}
					/>
				)}
			</Modal>
		</div>
	);
}
